import { Typography } from '@euler/app/components/typography';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AiHomeSession, AiHomeSessionsResult } from '@euler/model/ai-home';
import { useServiceFactory } from '@euler/services/factory';
import dayjs from 'dayjs';
import {
  History,
  MessageCircleMore,
  RefreshCw,
  Search,
  X,
} from 'lucide-react-native';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  SectionList,
  TextInput,
  View,
} from 'react-native';
import { aiColors, aiSuiteToolForSession, tint } from '../theme';

function dayLabel(value: string): string {
  const day = dayjs(value).startOf('day');
  const today = dayjs().startOf('day');
  const days = today.diff(day, 'day');
  return days === 0
    ? '今天'
    : days === 1
    ? '昨天'
    : days < 7
    ? '本周'
    : day.year() === today.year()
    ? day.format('M月')
    : day.format('YYYY年M月');
}

const kSearchDelayMs = 300;
/** The longest keyword the server accepts. */
const kKeywordMaxLength = 50;

type MoreState = 'idle' | 'loading' | 'failed';

/** The pages loaded so far, for the keyword they were searched with. */
type HistoryPage = AiHomeSessionsResult & { keyword: string };

/** The user's past 知车 AI conversations, newest first, grouped by day. */
export const AiHistoryList = memo(
  ({
    activeSessionId,
    refreshKey,
    onOpen,
    onClose,
  }: {
    activeSessionId?: string;
    /** Changes when a conversation was saved, so the list reloads. */
    refreshKey?: string;
    onOpen: (session: AiHomeSession) => void;
    onClose?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { aiAssistantService } = useServiceFactory();
    const [page, setPage] = useState<HistoryPage | null>(null);
    const [failed, setFailed] = useState(false);
    const [more, setMore] = useState<MoreState>('idle');
    const [query, setQuery] = useState('');
    const [keyword, setKeyword] = useState('');
    useEffect(() => {
      const timer = setTimeout(() => setKeyword(query.trim()), kSearchDelayMs);
      return () => clearTimeout(timer);
    }, [query]);
    // Answers to a superseded keyword, or a reload, are dropped.
    const request = useRef(0);
    // Set at once: the list can ask for the next page twice in one render.
    const moreInFlight = useRef(false);
    const load = useCallback(() => {
      const id = ++request.current;
      moreInFlight.current = false;
      setMore('idle');
      setFailed(false);
      setPage(null);
      aiAssistantService
        .listHomeSessions({ keyword, before: null })
        .then(result => {
          if (id === request.current) setPage({ keyword, ...result });
        })
        .catch(error => {
          console.error('AI home sessions failed', error);
          if (id === request.current) setFailed(true);
        });
    }, [aiAssistantService, keyword]);
    useEffect(load, [load, refreshKey]);
    const loadMore = useCallback(() => {
      if (!page?.next || moreInFlight.current) return;
      const id = request.current;
      moreInFlight.current = true;
      setMore('loading');
      aiAssistantService
        .listHomeSessions({ keyword: page.keyword, before: page.next })
        .then(result => {
          if (id !== request.current) return;
          moreInFlight.current = false;
          setMore('idle');
          setPage(
            current =>
              current && {
                ...current,
                sessions: [...current.sessions, ...result.sessions],
                next: result.next,
              },
          );
        })
        .catch(error => {
          console.error('AI home sessions failed', error);
          if (id !== request.current) return;
          moreInFlight.current = false;
          setMore('failed');
        });
    }, [aiAssistantService, page]);
    const sections = useMemo(() => {
      const groups: Array<{ title: string; data: AiHomeSession[] }> = [];
      for (const session of page?.sessions ?? []) {
        const title = dayLabel(session.updatedAt);
        const last = groups[groups.length - 1];
        if (last?.title === title) last.data.push(session);
        else groups.push({ title, data: [session] });
      }
      return groups;
    }, [page]);
    return (
      <View style={{ flex: 1, backgroundColor: aiColors.card }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(12),
            paddingHorizontal: rpx(20),
            paddingTop: rpx(20),
          }}
        >
          <View
            style={{
              width: rpx(40),
              height: rpx(40),
              borderRadius: rpx(12),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: aiColors.secondary,
            }}
          >
            <History size={20} color={aiColors.primary} />
          </View>
          <View style={{ flex: 1 }}>
            <Typography.Text bold size={rpx(18)} color={aiColors.foreground}>
              历史对话
            </Typography.Text>
            {page ? (
              <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
                {page.keyword
                  ? `找到 ${page.total} 条会话`
                  : `共 ${page.total} 条会话`}
              </Typography.Text>
            ) : null}
          </View>
          {onClose && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="关闭"
              onPress={onClose}
              hitSlop={8}
              style={{
                width: rpx(32),
                height: rpx(32),
                borderRadius: rpx(16),
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: aiColors.muted,
              }}
            >
              <X size={16} color={aiColors.mutedForeground} strokeWidth={2.2} />
            </Pressable>
          )}
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(8),
            marginHorizontal: rpx(20),
            marginTop: rpx(16),
            paddingHorizontal: rpx(12),
            height: rpx(40),
            borderRadius: rpx(12),
            borderWidth: 1,
            borderColor: aiColors.border,
            backgroundColor: aiColors.input,
          }}
        >
          <Search size={16} color={aiColors.mutedForeground} />
          <TextInput
            accessibilityLabel="搜索历史对话"
            value={query}
            onChangeText={setQuery}
            maxLength={kKeywordMaxLength}
            placeholder="搜索历史对话"
            placeholderTextColor={aiColors.mutedForeground}
            style={{
              flex: 1,
              fontSize: rpx(14),
              color: aiColors.foreground,
              padding: 0,
            }}
          />
        </View>
        {failed ? (
          <Pressable
            accessibilityRole="button"
            onPress={load}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: rpx(6),
              padding: rpx(24),
            }}
          >
            <RefreshCw size={14} color={aiColors.mutedForeground} />
            <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
              没有加载出来，点击重试
            </Typography.Text>
          </Pressable>
        ) : page == null ? (
          <ActivityIndicator
            color={aiColors.primary}
            style={{ margin: rpx(24) }}
          />
        ) : (
          <SectionList
            sections={sections}
            keyExtractor={session => session.id}
            stickySectionHeadersEnabled={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingBottom: rpx(24) }}
            onEndReached={loadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              more === 'loading' ? (
                <ActivityIndicator
                  color={aiColors.primary}
                  style={{ margin: rpx(16) }}
                />
              ) : more === 'failed' ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={loadMore}
                  style={{ padding: rpx(16), alignItems: 'center' }}
                >
                  <Typography.Text
                    size={rpx(13)}
                    color={aiColors.mutedForeground}
                  >
                    加载失败，点击重试
                  </Typography.Text>
                </Pressable>
              ) : null
            }
            ListEmptyComponent={
              <Typography.Text
                size={rpx(13)}
                color={aiColors.mutedForeground}
                style={{ textAlign: 'center', padding: rpx(32) }}
              >
                {page.keyword
                  ? '没有找到相关的对话。'
                  : '还没有对话。在首页说说您想做什么，就会出现在这里。'}
              </Typography.Text>
            }
            renderSectionHeader={({ section }) => (
              <Typography.Text
                size={rpx(12)}
                color={aiColors.mutedForeground}
                style={{
                  paddingHorizontal: rpx(20),
                  paddingTop: rpx(20),
                  paddingBottom: rpx(4),
                }}
              >
                {section.title}
              </Typography.Text>
            )}
            renderItem={({ item, section }) => (
              <SessionRow
                session={item}
                recent={section.title === '今天' || section.title === '昨天'}
                active={item.id === activeSessionId}
                onPress={() => onOpen(item)}
              />
            )}
          />
        )}
      </View>
    );
  },
);

/** One past conversation: what it was about, which tool, when. */
function SessionRow({
  session,
  recent,
  active,
  onPress,
}: {
  session: AiHomeSession;
  /** In today's or yesterday's group: the time alone says when. */
  recent: boolean;
  active: boolean;
  onPress: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const tool = aiSuiteToolForSession(session.bundleId);
  const Icon = tool?.icon ?? MessageCircleMore;
  const color = tool?.color ?? aiColors.mutedForeground;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(12),
        marginHorizontal: rpx(12),
        paddingHorizontal: rpx(8),
        paddingVertical: rpx(10),
        borderRadius: rpx(12),
        backgroundColor: active
          ? tint(aiColors.primary, 0.08)
          : pressed
          ? aiColors.muted
          : 'transparent',
      })}
    >
      <View
        style={{
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tool ? tint(color, 0.1) : aiColors.muted,
        }}
      >
        <Icon size={18} color={color} strokeWidth={1.8} />
      </View>
      <View style={{ flex: 1, gap: rpx(4) }}>
        <Typography.Text
          medium
          size={rpx(14)}
          color={aiColors.foreground}
          numberOfLines={1}
        >
          {session.title}
        </Typography.Text>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
        >
          {tool ? (
            <View
              style={{
                paddingHorizontal: rpx(6),
                paddingVertical: rpx(1),
                borderRadius: rpx(4),
                backgroundColor: tint(color, 0.1),
              }}
            >
              <Typography.Text medium size={rpx(11)} color={color}>
                {tool.title}
              </Typography.Text>
            </View>
          ) : null}
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {dayjs(session.updatedAt).format(recent ? 'HH:mm' : 'M月D日 HH:mm')}
          </Typography.Text>
        </View>
      </View>
    </Pressable>
  );
}

export const AiHistorySheet = memo(
  ({
    visible,
    side,
    activeSessionId,
    onOpen,
    onClose,
  }: {
    visible: boolean;
    /** A panel at the right edge on wide screens; a bottom sheet on phones. */
    side: boolean;
    activeSessionId?: string;
    onOpen: (session: AiHomeSession) => void;
    onClose: () => void;
  }) => {
    const list = (
      <AiHistoryList
        activeSessionId={activeSessionId}
        onOpen={session => {
          onClose();
          onOpen(session);
        }}
        onClose={onClose}
      />
    );
    if (!side)
      return (
        <BottomPanel visible={visible} onClose={onClose} tall>
          {list}
        </BottomPanel>
      );
    return (
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
      >
        <View style={{ flex: 1, flexDirection: 'row' }}>
          <Pressable
            accessibilityLabel="关闭"
            onPress={onClose}
            style={{ flex: 1, backgroundColor: 'rgba(15,23,42,0.4)' }}
          />
          <View
            style={{
              width: 360,
              height: '100%',
              backgroundColor: aiColors.card,
            }}
          >
            {visible && list}
          </View>
        </View>
      </Modal>
    );
  },
);
